import React, { useState } from 'react';
import Select from 'react-select';
import { obtenerEventosConTicketsSelect } from '../../../service/eventos/Eventos';
import { obtenerTicketsPorEventoService } from '../../../service/tickets/Tickets';
import { TicketCard } from './TicketCard';
import { TicketsPdf } from './TicketsPdf';

type EventoOption = {
  id: number;
  value: string;
  label: string;
};

const View: React.FC = () => {
  const [eventoOptions, setEventoOptions] = useState<EventoOption[]>([]);
  const [eventoSeleccionado, setEventoSeleccionado] = useState<EventoOption | null>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loadingEventos, setLoadingEventos] = useState<boolean>(false);
  const [loadingTickets, setLoadingTickets] = useState<boolean>(false);
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [folioMin, setFolioMin] = useState<string>('');
  const [folioMax, setFolioMax] = useState<string>('');

  const cargarEventos = async () => {
    if (eventoOptions.length > 0) return;
    setLoadingEventos(true);
    try {
      const data = await obtenerEventosConTicketsSelect();
      setEventoOptions(data);
    } catch {
      // silencioso
    } finally {
      setLoadingEventos(false);
    }
  };

  const handleSelectEvento = async (option: EventoOption | null) => {
    setEventoSeleccionado(option);
    setTickets([]);
    setShowPdf(false);
    if (!option) return;
    setLoadingTickets(true);
    try {
      const data = await obtenerTicketsPorEventoService(option.id);
      setTickets(Array.isArray(data) ? data : []);
    } catch {
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const ticketsFiltrados = tickets.filter((ticket: any) => {
    const folio = parseInt(ticket.folio, 10);
    const min = folioMin !== '' ? parseInt(folioMin, 10) : -Infinity;
    const max = folioMax !== '' ? parseInt(folioMax, 10) : Infinity;
    return folio >= min && folio <= max;
  });

  return (
    <div className="card">
      <div className="card-header border-0 pt-6">
        <div className="card-title">
          <h3 className="card-label fw-bolder text-dark">Generación de Tickets con QR</h3>
        </div>
      </div>

      <div className="card-body py-3">
        {/* Selector de evento y rango de folios */}
        <div className="row mb-6 align-items-end">
          <div className="col-md-4">
            <label className="form-label fw-bold">Seleccionar Evento</label>
            <Select
              placeholder="Selecciona un evento..."
              options={eventoOptions}
              value={eventoSeleccionado}
              onChange={(opt) => handleSelectEvento(opt as EventoOption | null)}
              onMenuOpen={cargarEventos}
              isLoading={loadingEventos}
              isClearable
            />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold">Folio desde</label>
            <input
              type="number"
              className="form-control"
              placeholder="Ej: 20000"
              value={folioMin}
              onChange={(e) => { setFolioMin(e.target.value); setShowPdf(false); }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold">Folio hasta</label>
            <input
              type="number"
              className="form-control"
              placeholder="Ej: 20500"
              value={folioMax}
              onChange={(e) => { setFolioMax(e.target.value); setShowPdf(false); }}
            />
          </div>
          {ticketsFiltrados.length > 0 && !showPdf && (
            <div className="col-md-3">
              <button
                className="btn btn-primary"
                onClick={() => setShowPdf(true)}
              >
                <i className="bi bi-printer me-2"></i>
                Imprimir ({ticketsFiltrados.length})
              </button>
            </div>
          )}
        </div>

        {/* Vista PDF */}
        {showPdf && ticketsFiltrados.length > 0 && (
          <div className="mb-6">
            <TicketsPdf
              tickets={ticketsFiltrados}
              handleClose={() => setShowPdf(false)}
            />
          </div>
        )}

        {/* Grid de tickets */}
        {!showPdf && (
          <>
            {loadingTickets && (
              <div className="d-flex justify-content-center py-10">
                <span className="indicator-progress" style={{ display: 'block' }}>
                  Cargando tickets...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </div>
            )}

            {!loadingTickets && eventoSeleccionado && tickets.length === 0 && (
              <div className="text-center text-muted py-10">
                No hay tickets activos para este evento.
              </div>
            )}

            {!loadingTickets && tickets.length > 0 && ticketsFiltrados.length === 0 && (
              <div className="text-center text-muted py-10">
                No hay tickets en el rango de folios indicado.
              </div>
            )}

            {!loadingTickets && ticketsFiltrados.length > 0 && (
              <>
                <div className="text-muted mb-3 fs-7">
                  {ticketsFiltrados.length} ticket(s) encontrado(s)
                  {(folioMin || folioMax) && (
                    <span className="ms-2 text-primary fw-bold">
                      (folios {folioMin || '—'} a {folioMax || '—'})
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  {ticketsFiltrados.map((ticket: any) => (
                    <TicketCard key={ticket.id} ticket={ticket} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export { View };
